import {Table, TableBody, TableCell, TableRow, Typography} from "@mui/material";
import {ReactNode} from "react";
import {FormattedMessage, MessageDescriptor} from "react-intl";

type ReadonlyFormTableEntryRenderer<T> = (value: T) => ReactNode;

type ReadonlyFormTableEntry<T> = {
    label: MessageDescriptor;
    render?: ReadonlyFormTableEntryRenderer<T>;
};

type ReadonlyFormTableProps<Data extends object> = {
    data: Data;
    fields: {
        [K in keyof Data]?: ReadonlyFormTableEntry<Data[K]>;
    };
};

const defaultRenderer: ReadonlyFormTableEntryRenderer<unknown> = (value) => <span>{value as ReactNode}</span>;

/**
 * A form-like table for displaying read-only data.
 * Used to display the same values as a form, but in a non-editable way, e.g. for someone without
 * edit permission.
 **/
function ReadonlyFormTable<Data extends object>({data, fields}: ReadonlyFormTableProps<Data>) {
    return (
        <Table sx={{"& td, & th": {border: 0}}}>
            <TableBody>
                {Object.entries<ReadonlyFormTableEntry<unknown>>(fields as any).map(([key, field]) => {
                    if (!field) {
                        return null;
                    }

                    const render = field.render || defaultRenderer;

                    return (
                        <TableRow key={key}>
                            <TableCell sx={{textAlign: "right", padding: 0}}>
                                <Typography
                                    sx={(theme) => ({fontWeight: theme.typography.fontWeightBold})}
                                    component="span"
                                >
                                    <FormattedMessage {...field.label} />:
                                </Typography>
                            </TableCell>
                            <TableCell sx={(theme) => ({padding: theme.spacing(1.5)})}>
                                {render((data as any)[key])}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}

export default ReadonlyFormTable;
